import parser from 'ua-parser-js'
import { includes, cond, max } from 'lodash-es'

import AlbumIndex, {
  NUMBER_PER_PAGE,
  DEFAULT_SORT,
} from 'components/pages/Album/Index'
import MetaTags from 'components/pages/Album/Index/MetaTags'
import NoindexRemoval from 'components/common/NoindexRemoval'
import withLivingSinyi from 'utils/withLivingSinyi'
import withSafeServerSideProps from 'utils/server-utils/with-safe-server-side-props'
import {
  getAlbums,
  getAlbumCount,
  getPhotoClassifications,
  getPhotoTags,
  getPhotoTagByUrlId,
} from 'utils/api'
import { safeDecodeURIComponent } from 'utils/helpers'

const AlbumPage = (props) => {
  return (
    <>
      <NoindexRemoval />
      <MetaTags
        resolvedUrl={props.resolvedUrl}
        album={props.fallbackData?.albums?.[0]}
        filters={props.fallbackData?.filters}
        searchText={props.fallbackData?.searchText}
        tag={props.fallbackData?.photoTag}
      />
      <AlbumIndex {...props} />
    </>
  )
}

export const getServerSidePropsFunction = async (context) => {
  const ua = parser(context.req.headers['user-agent'])

  const isMobile = includes(['mobile', 'tablet'], ua.device.type)

  const page = isMobile ? 1 : parseInt(context.query?.page) || 1

  const filters =
    typeof context.query?.tag === 'string'
      ? [context.query?.tag]
      : context.query?.tag || []

  const searchText = safeDecodeURIComponent(context.query?.keyword)

  const limit = NUMBER_PER_PAGE
  const sorts = DEFAULT_SORT.split(' ')
  const skip = isMobile ? 0 : max([page - 1, 0]) * NUMBER_PER_PAGE
  const albumsQuery = cond([
    [
      ({ search }) => !!search,
      ({ search }) => ({ search, sorts, limit, skip }),
    ],
    [
      ({ tagUrlIds }) => tagUrlIds?.length,
      ({ tagUrlIds }) => ({ tagUrlIds, sorts, limit, skip }),
    ],
    [() => true, () => ({ limit, sorts, skip })],
  ])({ search: searchText, tagUrlIds: filters })
  const albumsPromise = getAlbums(albumsQuery)

  const {
    limit: albumLimit,
    sorts: albumSorts,
    ...albumCountQuery
  } = albumsQuery
  const albumCountPromise =
    !isMobile && (searchText || filters?.length)
      ? getAlbumCount(albumCountQuery)
      : Promise.resolve([null])

  const photoClassificationsPromise = getPhotoClassifications()

  const photoTagsPromise =
    !isMobile && !!searchText
      ? getPhotoTags({ tagName: searchText })
      : Promise.resolve([[]])

  const tagPromise = filters?.length
    ? getPhotoTagByUrlId(filters?.[0])
    : Promise.resolve([null])

  const [
    albumsResult,
    albumCountResult,
    photoClassificationsResult,
    photoTagsResult,
    tagResult,
  ] = await Promise.all([
    albumsPromise,
    albumCountPromise,
    photoClassificationsPromise,
    photoTagsPromise,
    tagPromise,
  ])
  const [albums, albumsError] = albumsResult
  const [albumCount, albumsCountError] = albumCountResult
  const [
    photoClassifications,
    photoClassificationsError,
  ] = photoClassificationsResult
  const [photoTags, photoTagsError] = photoTagsResult

  const [photoTag, photoTagError] = tagResult

  if (albumsError) {
    // albums undefined 會造成 Next 序列化 500（意外的錯誤頁），丟給 withSafeServerSideProps 分流
    throw albumsError
  }
  if (photoClassificationsError || albumsCountError || photoTagsError) {
    const logger = require('utils/server-utils/logger')
    logger.warn({
      event: 'gssp_fetch_error_degraded',
      page: context.resolvedUrl,
      reason: (photoClassificationsError || albumsCountError || photoTagsError)
        ?.message,
    })
  }

  return {
    props: {
      isMobile,
      fallbackData: {
        page,
        filters,
        searchText,

        // fetched data
        albums: albums ?? [],
        albumCount: albumCount ?? null,
        photoClassifications: photoClassifications ?? [],
        photoTags: photoTags ?? [],
        photoTag: photoTag ?? null, // first tag from query string `tag=xxx`
      },
      resolvedUrl: context.resolvedUrl,
    },
  }
}

export const getServerSideProps = withSafeServerSideProps(
  getServerSidePropsFunction
)

export default withLivingSinyi(AlbumPage)
